import React, { useState, useEffect, useRef } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  Animated,
  RefreshControl,
  StatusBar,
  Alert,
  Image,
  Modal,
  TextInput,
  FlatList,
  SectionList,
  ActivityIndicator,
  BackHandler,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
  MaterialIcons,
  Feather,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCrossPortalSync } from './CrossPortalIntegration';
import { useAutomatedWorkflow } from './AutomatedHiringWorkflow';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


const { width, height } = Dimensions.get('window');

// UPDATED: Enhanced MultiUserBusinessDashboard with preserved functionality
export default function MultiUserBusinessDashboard({ navigation }) {
  // Core state management - PRESERVED
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState({
    plan_id: 'standard',
    plan_type: 'multi_user',
    price: 0,
    users: 0,
    features: [],
    billing_cycle: 'monthly',
  });
  const [companyProfile, setCompanyProfile] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // UI state management - PRESERVED
  const [activeTab, setActiveTab] = useState('overview');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBusinessSolutions, setShowBusinessSolutions] = useState(false);
  
  // UPDATED: Enhanced navigation state for role-based routing
  const [userRole, setUserRole] = useState('Business Owner'); // Business Owner, Manager, Employee
  const [subscriptionType, setSubscriptionType] = useState('multi_user'); // single, multi_user
  const [availableDashboards, setAvailableDashboards] = useState([]);
  
  // Business data state - PRESERVED
  const [businessStats, setBusinessStats] = useState({
    activeJobs: 12,
    applications: 156,
    interviews: 8,
    offers: 3,
    projects: 15,
    teamMembers: 5,
    revenue: 245000,
    clients: 45,
    vendors: 23,
    contracts: 18,
    invoices: 12,
    campaigns: 6
  });
  
  // Notifications state - PRESERVED
  const [notifications, setNotifications] = useState([
    { id: 1, title: 'New Application', message: 'John Smith applied for Senior Engineer', time: '5 min ago', type: 'application', priority: 'high' },
    { id: 2, title: 'Interview Scheduled', message: 'Interview with Sarah Johnson tomorrow at 2 PM', time: '1 hour ago', type: 'interview', priority: 'medium' },
    { id: 3, title: 'Project Update', message: 'Bridge Construction project 75% complete', time: '2 hours ago', type: 'project', priority: 'low' },
    { id: 4, title: 'Payment Received', message: 'Invoice #INV-2024-001 paid by ABC Corp', time: '3 hours ago', type: 'payment', priority: 'medium' },
    { id: 5, title: 'Team Member Added', message: 'Mike Johnson joined as Project Manager', time: '1 day ago', type: 'team', priority: 'low' }
  ]);

  // Animation values - PRESERVED
  const slideAnim = useRef(new Animated.Value(0)).current;
  const fadeAnim = useRef(new Animated.Value(1)).current;

  // Cross-portal sync and automated workflow - PRESERVED
  const crossPortalSync = useCrossPortalSync();
  const automatedWorkflow = useAutomatedWorkflow();

  useEffect(() => {
    initializeDashboard();
    
    // Handle hardware back button - PRESERVED
    const backHandler = BackHandler.addEventListener(
      'hardwareBackPress',
      handleBackPress
    );
    
    // Auto-refresh data every 30 seconds - PRESERVED
    const refreshInterval = setInterval(() => {
      updateBusinessStats();
    }, 30000);
    
    return () => {
      backHandler.remove();
      clearInterval(refreshInterval);
    };
  }, []);

  // UPDATED: Enhanced dashboard initialization with role-based setup
  const initializeDashboard = async () => {
    try {
      setIsLoading(true);
      
      // Initialize cross-portal sync and automated workflow - PRESERVED
      if (crossPortalSync && crossPortalSync.initialize) {
        crossPortalSync.initialize();
      }
      if (automatedWorkflow && automatedWorkflow.initialize) {
        automatedWorkflow.initialize();
      }
      
      // Load user data - COORDINATED WITH WELCOME SCREEN
      let userData = await AsyncStorage.getItem('user_data');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        
        // UPDATED: Set user role from data
        const role = parsedUser.user_role || parsedUser.role || 'Employee';
        setUserRole(role);
      } else {
        // Fallback user data - PRESERVED
        const defaultUser = {
          id: 1,
          full_name: 'John Smith',
          email: 'john.smith@jobeng.com',
          user_role: 'Business Owner', // Default role
          job_title: 'Senior Project Manager',
          department: 'Engineering',
          company_name: 'JOBENG Construction Ltd',
          company_id: 1,
        };
        setUser(defaultUser);
        setUserRole(defaultUser.user_role);
      }
      
      // Load subscription data - COORDINATED WITH WELCOME SCREEN
      // Try API first for subscription
      let subscriptionData = null;
      try {
        const resp = await apiRequest('business-subscriptions');
        if (resp.success && resp.data) {
          subscriptionData = JSON.stringify(resp.data);
          await AsyncStorage.setItem('business_subscription', subscriptionData);
        }
      } catch (apiErr) {
        subscriptionData = await AsyncStorage.getItem('business_subscription');
      }
      if (subscriptionData) {
        const parsedSubscription = JSON.parse(subscriptionData);
        setSubscription(parsedSubscription);
        
        // UPDATED: Determine subscription type
        const subType = parsedSubscription.plan_type === 'single_user' ? 'single' : 'multi_user';
        setSubscriptionType(subType);
      } else {
        // Default subscription for demo - PRESERVED
        const defaultSubscription = {
          plan_id: 'multi_business',
          plan_type: 'multi_user',
          price: 299,
          billing_cycle: 'monthly',
          users: 15,
          features: ['Unlimited jobs', 'Advanced analytics', 'Team collaboration', 'API access']
        };
        setSubscription(defaultSubscription);
        setSubscriptionType('multi_user');
      }
      
      // UPDATED: Set available dashboards based on role and subscription
      setAvailableDashboards(getAvailableDashboards(userRole, subscriptionType));
      
      // Load company profile - PRESERVED
      // Try API first for company profile
      let companyData = null;
      try {
        const resp = await apiRequest('company-profiles');
        if (resp.success && resp.data) {
          companyData = JSON.stringify(resp.data);
          await AsyncStorage.setItem('company_profile', companyData);
        }
      } catch (apiErr) {
        companyData = await AsyncStorage.getItem('company_profile');
      }
      if (companyData) {
        setCompanyProfile(JSON.parse(companyData));
      } else {
        // Default company profile - PRESERVED
        setCompanyProfile({
          name: 'JOBENG Construction Ltd',
          industry: 'Construction & Engineering',
          headquarters: 'London, UK',
          established: 2015,
          employees: 85,
          revenue: '£2.4M',
          description: 'Leading construction and engineering company specializing in commercial and residential projects.',
          specializations: ['Commercial Construction', 'Residential Development', 'Infrastructure Projects', 'Green Building Solutions'],
          logo: null
        });
      }
      
      // Load team members - PRESERVED
      // Try API first for team members
      let teamData = null;
      try {
        const resp = await apiRequest('team-members');
        if (resp.success && resp.data) {
          teamData = JSON.stringify(resp.data);
          await AsyncStorage.setItem('team_members', teamData);
        }
      } catch (apiErr) {
        teamData = await AsyncStorage.getItem('team_members');
      }
      if (teamData) {
        setTeamMembers(JSON.parse(teamData));
      } else {
        // Default team members - PRESERVED
        setTeamMembers([
          { id: 1, name: 'John Smith', role: 'Administrator', department: 'Management', status: 'online', email: 'john@jobeng.com' },
          { id: 2, name: 'Sarah Johnson', role: 'HR Manager', department: 'Human Resources', status: 'online', email: 'sarah@jobeng.com' },
          { id: 3, name: 'Mike Wilson', role: 'Project Manager', department: 'Engineering', status: 'away', email: 'mike@jobeng.com' },
          { id: 4, name: 'Emily Davis', role: 'Finance Manager', department: 'Finance', status: 'online', email: 'emily@jobeng.com' },
          { id: 5, name: 'David Brown', role: 'Employee', department: 'Engineering', status: 'offline', email: 'david@jobeng.com' }
        ]);
      }
      
      // Update business stats - PRESERVED
      await updateBusinessStats();
      
    } catch (error) {
      console.error('Error initializing dashboard:', error);
      Alert.alert('Error', 'Failed to load dashboard data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // NEW: Get available dashboards based on role and subscription
  const getAvailableDashboards = (role, subscriptionType) => {
    const dashboards = [];
    
    if (subscriptionType === 'single') {
      // Single subscription users only get MainDashboard
      dashboards.push({
        id: 'main',
        title: 'Main Dashboard',
        screen: 'BusinessDashboard',
        description: 'Primary business dashboard',
        icon: 'view-dashboard',
        color: '#4A90E2'
      });
    } else {
      // Multi-user subscription users get role-appropriate dashboards
      switch (role) {
        case 'Business Owner':
        case 'Administrator':
        case 'admin':
          dashboards.push({
            id: 'main',
            title: 'Main Dashboard',
            screen: 'BusinessDashboard',
            description: 'Primary business dashboard with full access',
            icon: 'view-dashboard',
            color: '#4A90E2'
          });
          break;
          
        case 'Manager':
        case 'HR Manager':
        case 'Project Manager':
          dashboards.push({
            id: 'manager',
            title: 'Manager Dashboard',
            screen: 'ManagerDashboard',
            description: 'Management tools and team oversight',
            icon: 'account-supervisor',
            color: '#FF9800'
          });
          break;
          
        case 'Employee':
        default:
          dashboards.push({
            id: 'employee',
            title: 'Employee Dashboard',
            screen: 'EmployeeDashboard',
            description: 'Employee tools and personal workspace',
            icon: 'account',
            color: '#4CAF50'
          });
          break;
      }
    }
    
    return dashboards;
  };

  // NEW: Navigate to appropriate dashboard based on role and subscription
  const navigateToDashboard = (dashboardId) => {
    const dashboard = availableDashboards.find(d => d.id === dashboardId);
    if (dashboard) {
      navigateToScreen(dashboard.screen, { 
        userRole, 
        subscriptionType,
        user,
        subscription 
      });
    } else {
      Alert.alert('Access Denied', 'This dashboard is not available for your role or subscription.');
    }
  };

  // Handle back button press - PRESERVED
  const handleBackPress = () => {
    if (showNotifications || showQuickActions || showTeamModal || showSearchModal || showBusinessSolutions) {
      setShowNotifications(false);
      setShowQuickActions(false);
      setShowTeamModal(false);
      setShowSearchModal(false);
      setShowBusinessSolutions(false);
      return true;
    }
    return false;
  };

  // Update business statistics - PRESERVED
  const updateBusinessStats = async () => {
    try {
      // Simulate API calls to get real business stats
      const stats = {
        activeJobs: Math.floor(Math.random() * 20) + 8,
        applications: Math.floor(Math.random() * 200) + 100,
        interviews: Math.floor(Math.random() * 15) + 5,
        offers: Math.floor(Math.random() * 10) + 2,
        projects: Math.floor(Math.random() * 25) + 12,
        teamMembers: teamMembers.length || 5,
        revenue: Math.floor(Math.random() * 500000) + 200000,
        clients: Math.floor(Math.random() * 100) + 30,
        vendors: Math.floor(Math.random() * 50) + 20,
        contracts: Math.floor(Math.random() * 30) + 15,
        invoices: Math.floor(Math.random() * 20) + 8,
        campaigns: Math.floor(Math.random() * 10) + 3
      };
      
      setBusinessStats(stats);
      
      // Save updated stats
      try { await apiRequest('business-stats', { method: 'PUT', body: stats }); } catch(e) { console.warn('API save failed:', e.message); }
      await AsyncStorage.setItem('business_stats', JSON.stringify(stats));
    } catch (error) {
      console.error('Error updating business stats:', error);
    }
  };

  // Handle refresh - PRESERVED
  const onRefresh = async () => {
    setRefreshing(true);
    await updateBusinessStats();
    await initializeDashboard();
    setRefreshing(false);
  };

  // Utility functions - PRESERVED
  const getUserInitials = (name) => {
    if (!name || typeof name !== 'string') return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getFirstName = (name) => {
    if (!name || typeof name !== 'string') return 'User';
    return name.split(' ')[0];
  };

  // Check if feature is available based on subscription - PRESERVED
  const isFeatureAvailable = (requiredPlan) => {
    if (!subscription) return false;
    
    const planHierarchy = {
      'single_starter': 1,
      'single_professional': 2,
      'single_enterprise': 3,
      'multi_team': 2,
      'multi_business': 3,
      'multi_enterprise': 4
    };
    
    const requiredPlanMap = {
      'starter': 1,
      'professional': 2,
      'enterprise': 3,
      'team': 2
    };
    
    return (planHierarchy[subscription?.plan_id] || 0) >= (requiredPlanMap[requiredPlan] || 0);
  };

  // Navigation helper with safety checks - PRESERVED
  const navigateToScreen = (screenName, params = {}) => {
    if (navigation && navigation.navigate) {
      navigation.navigate(screenName, params);
    } else {
      Alert.alert('Navigation Error', `Cannot navigate to ${screenName}. Navigation not available.`);
    }
  };

  // NEW: Render dashboard selection section
  const renderDashboardSelection = () => (
    <View style={styles.dashboardSection}>
      <Text style={styles.sectionTitle}>Available Dashboards</Text>
      <Text style={styles.sectionSubtitle}>
        Based on your role: {userRole} • Subscription: {(subscriptionType || '').replace('_', ' ')}
      </Text>
      
      <View style={styles.dashboardGrid}>
        {availableDashboards.map(dashboard => (
          <TouchableOpacity
            key={dashboard.id}
            style={[styles.dashboardCard, { borderColor: dashboard.color }]}
            onPress={() => navigateToDashboard(dashboard.id)}
          >
            <View style={[styles.dashboardIcon, { backgroundColor: `${dashboard.color}20` }]}>
              <MaterialCommunityIcons 
                name={dashboard.icon} 
                size={32} 
                color={dashboard.color} 
              />
            </View>
            <Text style={styles.dashboardTitle}>{dashboard.title}</Text>
            <Text style={styles.dashboardDescription}>{dashboard.description}</Text>
            
            <View style={styles.dashboardAction}>
              <Text style={[styles.dashboardActionText, { color: dashboard.color }]}>
                Access Dashboard
              </Text>
              <Ionicons name="arrow-forward" size={16} color={dashboard.color} />
            </View>
          </TouchableOpacity>
        ))}
      </View>
      
      {subscriptionType === 'single' && (
        <View style={styles.upgradePrompt}>
          <MaterialCommunityIcons name="information" size={20} color="#FF9800" />
          <Text style={styles.upgradePromptText}>
            Upgrade to Multi-User subscription to access role-specific dashboards
          </Text>
          <TouchableOpacity 
            style={styles.upgradeButton}
            onPress={() => navigateToScreen('SubscriptionUpgradeScreen')}
          >
            <Text style={styles.upgradeButtonText}>Upgrade</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );

  // PRESERVED: Render header with company info
  const renderHeader = () => (
    <LinearGradient
      colors={['#4A90E2', '#357ABD']}
      style={styles.header}
    >
      <StatusBar barStyle="light-content" backgroundColor="#4A90E2" />
      
      <View style={styles.headerContent}>
        {/* Welcome Message */}
        <TouchableOpacity 
          style={styles.welcomeSection}
          onPress={() => navigateToScreen('WelcomeBusinessScreen')}
        >
          <Text style={styles.welcomeText}>
            Welcome back, {getFirstName(user?.full_name || 'User')}!
          </Text>
          <Text style={styles.welcomeSubtext}>
            {userRole} • {companyProfile?.name || 'Your Company'}
          </Text>
        </TouchableOpacity>
        
        {/* Company Info - COORDINATED WITH WELCOME SCREEN */}
        <View style={styles.companyInfo}>
          <Text style={styles.companyName}>
            {companyProfile?.name || user?.company_name || 'JOBENG Construction Ltd'}
          </Text>
          <Text style={styles.companyType}>
            {companyProfile?.industry || 'Construction & Engineering'}
          </Text>
          
          {/* Company Stats - SAME AS WELCOME SCREEN */}
          <View style={styles.companyStats}>
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>{businessStats.teamMembers}</Text>
              <Text style={styles.statLabel}>Employees</Text>
            </View>
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>2015</Text>
              <Text style={styles.statLabel}>Established</Text>
            </View>
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>{businessStats.projects}</Text>
              <Text style={styles.statLabel}>Projects</Text>
            </View>
          </View>
        </View>
        
        {/* Header Actions */}
        <View style={styles.headerActions}>
          <TouchableOpacity 
            style={styles.headerButton}
            onPress={() => setShowSearchModal(true)}
          >
            <Ionicons name="search" size={20} color="#fff" />
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.headerButton}
            onPress={() => setShowNotifications(true)}
          >
            <Ionicons name="notifications" size={20} color="#fff" />
            {notifications.length > 0 && (
              <View style={styles.notificationBadge}>
                <Text style={styles.notificationBadgeText}>{notifications.length}</Text>
              </View>
            )}
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.profileButton}
            onPress={() => navigateToScreen('BusinessUserProfileScreen')}
          >
            <View style={styles.profileAvatar}>
              <Text style={styles.profileInitials}>
                {getUserInitials(user?.full_name || 'User')}
              </Text>
            </View>
          </TouchableOpacity>
        </View>
      </View>
      
      {/* Revenue and Clients Stats - SAME AS WELCOME SCREEN */}
      <View style={styles.metricsContainer}>
        <View style={styles.metricCard}>
          <Text style={styles.metricValue}>£{(businessStats.revenue / 1000).toFixed(1)}K</Text>
          <Text style={styles.metricLabel}>Annual Revenue</Text>
          <View style={styles.metricChange}>
            <Ionicons name="trending-up" size={12} color="#10b981" />
            <Text style={styles.metricChangeText}>+12.5%</Text>
          </View>
        </View>
        
        <View style={styles.metricCard}>
          <Text style={styles.metricValue}>{businessStats.clients}</Text>
          <Text style={styles.metricLabel}>Active Clients</Text>
          <View style={styles.metricChange}>
            <Ionicons name="trending-up" size={12} color="#10b981" />
            <Text style={styles.metricChangeText}>+8%</Text>
          </View>
        </View>
      </View>
      
      {/* Action Buttons - SAME AS WELCOME SCREEN */}
      <View style={styles.actionButtons}>
        <TouchableOpacity 
          style={styles.actionButton}
          onPress={() => navigateToScreen('CompanyProfileScreen')}
        >
          <MaterialCommunityIcons name="account-edit" size={16} color="#fff" />
          <Text style={styles.actionButtonText}>Edit Profile</Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.actionButton}
          onPress={() => navigateToScreen('CompanyEventsScreen')}
        >
          <MaterialCommunityIcons name="calendar" size={16} color="#fff" />
          <Text style={styles.actionButtonText}>Events</Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.actionButton}
          onPress={() => setShowTeamModal(true)}
        >
          <MaterialCommunityIcons name="account-group" size={16} color="#fff" />
          <Text style={styles.actionButtonText}>Team</Text>
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );

  // Loading screen - PRESERVED
  if (isLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#4A90E2" />
          <Text style={styles.loadingText}>Loading dashboard...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      {renderHeader()}
      
      <ScrollView 
        style={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      >
        {renderDashboardSelection()}
        
        {/* Subscription Status - PRESERVED */}
        {subscription && (
          <View style={styles.subscriptionSection}>
            <View style={styles.subscriptionCard}>
              <View style={styles.subscriptionHeader}>
                <MaterialCommunityIcons name="crown" size={24} color="#FFD700" />
                <Text style={styles.subscriptionTitle}>
                  {(subscription?.plan_id || 'standard').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Plan
                </Text>
              </View>
              <Text style={styles.subscriptionPrice}>
                £{subscription?.price || 0}/month • {subscription?.users || 0} users
              </Text>
              <View style={styles.subscriptionFeatures}>
                {(subscription?.features || []).slice(0, 3).map((feature, index) => (
                  <Text key={index} style={styles.subscriptionFeature}>
                    • {feature}
                  </Text>
                ))}
              </View>
            </View>
          </View>
        )}
        
        {/* Quick Stats Overview - PRESERVED */}
        <View style={styles.quickStatsSection}>
          <Text style={styles.sectionTitle}>Quick Overview</Text>
          <View style={styles.quickStatsGrid}>
            <View style={styles.quickStatCard}>
              <MaterialCommunityIcons name="briefcase" size={24} color="#2196F3" />
              <Text style={styles.quickStatNumber}>{businessStats.activeJobs}</Text>
              <Text style={styles.quickStatLabel}>Active Jobs</Text>
            </View>
            <View style={styles.quickStatCard}>
              <MaterialCommunityIcons name="account-multiple" size={24} color="#4CAF50" />
              <Text style={styles.quickStatNumber}>{businessStats.applications}</Text>
              <Text style={styles.quickStatLabel}>Applications</Text>
            </View>
            <View style={styles.quickStatCard}>
              <MaterialCommunityIcons name="calendar-clock" size={24} color="#FF9800" />
              <Text style={styles.quickStatNumber}>{businessStats.interviews}</Text>
              <Text style={styles.quickStatLabel}>Interviews</Text>
            </View>
            <View style={styles.quickStatCard}>
              <MaterialCommunityIcons name="handshake" size={24} color="#E91E63" />
              <Text style={styles.quickStatNumber}>{businessStats.offers}</Text>
              <Text style={styles.quickStatLabel}>Offers</Text>
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

// PRESERVED: All original styles with new additions
const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#64748B',
    marginTop: 12,
  },
  header: {
    paddingTop: 10,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  welcomeSection: {
    flex: 1,
  },
  welcomeText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 4,
  },
  welcomeSubtext: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  companyInfo: {
    alignItems: 'center',
    flex: 2,
  },
  companyName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
    textAlign: 'center',
    marginBottom: 4,
  },
  companyType: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    textAlign: 'center',
    marginBottom: 12,
  },
  companyStats: {
    flexDirection: 'row',
    gap: 16,
  },
  statItem: {
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  statLabel: {
    fontSize: 10,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  notificationBadge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: '#E91E63',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  notificationBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#fff',
  },
  profileButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  profileAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  profileInitials: {
    fontSize: 14,
    fontWeight: '600',
    color: '#4A90E2',
  },
  metricsContainer: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  metricCard: {
    flex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
  },
  metricValue: {
    fontSize: 20,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 4,
  },
  metricLabel: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    marginBottom: 8,
  },
  metricChange: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metricChangeText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#10b981',
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    gap: 6,
  },
  actionButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#fff',
  },
  content: {
    flex: 1,
  },
  // NEW: Dashboard selection styles
  dashboardSection: {
    padding: 20,
    backgroundColor: '#fff',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 8,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 20,
  },
  dashboardGrid: {
    gap: 16,
  },
  dashboardCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 20,
    borderWidth: 2,
    borderColor: '#E2E8F0',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  dashboardIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  dashboardTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 8,
  },
  dashboardDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    marginBottom: 16,
  },
  dashboardAction: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dashboardActionText: {
    fontSize: 14,
    fontWeight: '500',
  },
  upgradePrompt: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF8E1',
    borderRadius: 12,
    padding: 16,
    marginTop: 16,
    gap: 12,
  },
  upgradePromptText: {
    flex: 1,
    fontSize: 14,
    color: '#F57C00',
  },
  upgradeButton: {
    backgroundColor: '#FF9800',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  upgradeButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
  },
  // PRESERVED: Subscription and stats styles
  subscriptionSection: {
    padding: 20,
    backgroundColor: '#fff',
    marginBottom: 16,
  },
  subscriptionCard: {
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  subscriptionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  subscriptionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
  },
  subscriptionPrice: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 12,
  },
  subscriptionFeatures: {
    gap: 4,
  },
  subscriptionFeature: {
    fontSize: 12,
    color: '#64748B',
  },
  quickStatsSection: {
    padding: 20,
    backgroundColor: '#fff',
    marginBottom: 16,
  },
  quickStatsGrid: {
    flexDirection: 'row',
    gap: 12,
  },
  quickStatCard: {
    flex: 1,
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  quickStatNumber: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginTop: 8,
    marginBottom: 4,
  },
  quickStatLabel: {
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
  },
});